import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from 'antd';
import type {
  Appointment,
  AppointmentComponentProps,
} from 'pos-client/components/customScheduler/types/schedulerTypes';

import styles from './OverlapBubble.module.css';

interface OverlapBubbleProps {
  hiddenCount: number;
  hiddenAppointments: Appointment[];
  appointmentComponent?: React.ComponentType<AppointmentComponentProps>;
  onAppointmentClick?: (data: {
    appointmentData: Appointment;
    appointmentElement: HTMLDivElement;
  }) => void;
  onAppointmentContextMenu?: (data: {
    appointmentData: Appointment;
    appointmentElement: HTMLDivElement;
    event: MouseEvent;
  }) => void;
}

const OverlapBubble: React.FC<OverlapBubbleProps> = ({
  hiddenCount,
  hiddenAppointments,
  appointmentComponent: AppointmentComponent,
  onAppointmentClick,
  onAppointmentContextMenu,
}) => {
  const [open, setOpen] = useState(false);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on click outside bubble + popover
  useEffect(() => {
    if (!open) return;

    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        bubbleRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    // Use setTimeout so the current click event doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleDocumentClick);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [open]);

  const handleBubbleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setOpen((prev) => !prev);
    },
    []
  );

  const handleBubbleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
    },
    []
  );

  const handleBubbleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && open) {
        e.stopPropagation();
        setOpen(false);
        bubbleRef.current?.focus();
      }
    },
    [open]
  );

  const handlePopoverKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        bubbleRef.current?.focus();
      }
    },
    []
  );

  const handleItemClick = useCallback(
    (appointment: Appointment, e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onAppointmentClick?.({
        appointmentData: appointment,
        appointmentElement: e.currentTarget,
      });
    },
    [onAppointmentClick]
  );

  const handleItemContextMenu = useCallback(
    (appointment: Appointment, e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onAppointmentContextMenu?.({
        appointmentData: appointment,
        appointmentElement: e.currentTarget,
        event: e.nativeEvent as MouseEvent,
      });
    },
    [onAppointmentContextMenu]
  );

  if (hiddenCount === 0) {
    return null;
  }

  const popoverContent = (
    <div
      ref={popoverRef}
      className={styles.popoverContent}
      role="dialog"
      aria-label={`${hiddenCount} more appointments`}
      onKeyDown={handlePopoverKeyDown}
    >
      {hiddenAppointments.map((appointment, index) => (
        <div
          key={appointment.id ?? index}
          className={styles.popoverItem}
          tabIndex={0}
          onClick={(e) => handleItemClick(appointment, e)}
          onContextMenu={(e) => handleItemContextMenu(appointment, e)}
        >
          <div className={styles.popoverItemInner}>
            {AppointmentComponent ? (
              <AppointmentComponent
                data={appointment}
                index={index}
                userIsDragging={false}
              />
            ) : (
              <div>{appointment.title}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      trigger={[]}
      open={open}
      overlayStyle={{ minWidth: 280 }}
    >
      <div
        ref={bubbleRef}
        className={styles.bubble}
        tabIndex={0}
        aria-label={`${hiddenCount} more appointments`}
        onClick={handleBubbleClick}
        onContextMenu={handleBubbleContextMenu}
        onKeyDown={handleBubbleKeyDown}
      >
        {hiddenCount}
      </div>
    </Popover>
  );
};

export default React.memo(OverlapBubble);
